import { supabase } from './supabase';
import type {
  Agent,
  BudgetState,
  ChatMessage,
  KnowledgeSource,
  L2uResource,
  ModelOption,
  UsageRequestRow,
  UsageTotals,
  WorkspaceSettings,
  WorkspaceUser,
} from '@shared/types';

export interface AgentWithCounts extends Agent {
  sourceCount: number;
}

export interface WorkspaceResponse {
  customer: { name: string; short: string };
  settings: WorkspaceSettings;
  budget: BudgetState;
  l2uApiMode: 'mock' | 'live';
  models: ModelOption[];
}

export interface UsageResponse {
  totals: UsageTotals;
  requests: UsageRequestRow[];
  perAgent: Array<{ agentId: string; name: string; count: number }>;
}

export interface SendResponse {
  conversationId: string;
  message: ChatMessage;
  usage: { inputTokens: number; outputTokens: number; costUsd: number; model: string };
  budget: BudgetState;
}

export interface TestConnectionResponse {
  ok: boolean;
  message: string;
  stops: Array<{ id: string; customer: string; status: string; eta: string }>;
  mode: 'mock' | 'live';
}

/** Surfaces the API's own message so the UI can show something useful. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  // Let the browser set the boundary for multipart bodies.
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }

  const res = await fetch(path, { ...init, headers });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; detail?: string };
      if (body.error) message = body.error;
      detail = body.detail;
    } catch {
      /* non-JSON error body; keep the generic message */
    }
    throw new ApiError(res.status, message, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  workspace: () => request<WorkspaceResponse>('/api/workspace'),

  updateWorkspace: (patch: Partial<WorkspaceSettings> & { l2uApiKey?: string }) =>
    request<{ settings: WorkspaceSettings; budget: BudgetState }>('/api/workspace', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  testConnection: () => request<TestConnectionResponse>('/api/connection/test', { method: 'POST' }),

  agents: () => request<{ agents: AgentWithCounts[] }>('/api/agents'),

  createAgent: (payload: Partial<Agent>) =>
    request<{ agent: Agent }>('/api/agents', { method: 'POST', body: JSON.stringify(payload) }),

  updateAgent: (id: string, payload: Partial<Agent>) =>
    request<{ agent: Agent }>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteAgent: (id: string) => request<{ ok: true }>(`/api/agents/${id}`, { method: 'DELETE' }),

  messages: (agentId: string) =>
    request<{ conversationId: string | null; messages: ChatMessage[] }>(
      `/api/agents/${agentId}/messages`,
    ),

  send: (agentId: string, message: string, conversationId: string | null) =>
    request<SendResponse>('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ agentId, message, conversationId }),
    }),

  knowledge: () => request<{ sources: KnowledgeSource[] }>('/api/knowledge'),

  addTextSource: (payload: { name: string; content: string; agentIds: string[] }) =>
    request<{ source: KnowledgeSource; chunks: number }>('/api/knowledge', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  uploadSource: (file: File, agentIds: string[]) => {
    const form = new FormData();
    form.append('file', file);
    form.append('agentIds', JSON.stringify(agentIds));
    return request<{ source: KnowledgeSource; chunks: number }>('/api/knowledge/upload', {
      method: 'POST',
      body: form,
    });
  },

  /** The list response omits `content`; this is how the editor reads the text. */
  getSource: (id: string) =>
    request<{ source: KnowledgeSource; content: string }>(`/api/knowledge/${id}`),

  assignSource: (id: string, agentIds: string[]) =>
    request<{ source: KnowledgeSource }>(`/api/knowledge/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ agentIds }),
    }),

  /** `chunks` comes back only when the text changed and was re-indexed. */
  updateSource: (id: string, payload: { name?: string; content?: string }) =>
    request<{ source: KnowledgeSource; chunks?: number }>(`/api/knowledge/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteSource: (id: string) => request<{ ok: true }>(`/api/knowledge/${id}`, { method: 'DELETE' }),

  usage: () => request<UsageResponse>('/api/usage'),

  /** Administrators only; 403s for a member. */
  users: () => request<{ users: WorkspaceUser[] }>('/api/users'),

  /** `warning` is set when the account was created but the role update failed. */
  inviteUser: (payload: { email: string; role: 'member' | 'admin'; displayName?: string }) =>
    request<{ user: WorkspaceUser; warning?: string }>('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** 409s rather than leaving the workspace without an administrator. */
  updateUserRole: (id: string, role: 'member' | 'admin') =>
    request<{ user: WorkspaceUser }>(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  /** Deletes the account. 409s on your own, and on the last administrator. */
  removeUser: (id: string) => request<{ ok: true }>(`/api/users/${id}`, { method: 'DELETE' }),

  me: () => request<{ user: { id: string; email: string; role: 'member' | 'admin' } }>('/api/me'),
};

export type { L2uResource };
